import { Fragment, useEffect, useState } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { LoadingOutlined } from '@ant-design/icons';
import { cartApi } from '../../../api/cart';
import { LoadingIcon } from '../../components/Spinner';
import { message, Spin } from 'antd';
import { centsToDollars } from '../../utils';

const PaymentForm = ({
  clientSecret,
  onSuccess,
  onError,
  onCancel,
  amount,
  description,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async e => {
    e.preventDefault();

    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(null);

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment(
        {
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/payment/complete`,
          },
          redirect: 'if_required',
        },
      );

      if (submitError) {
        console.log(submitError);
        setError(submitError.message);
        onError?.(submitError);
      } else if (paymentIntent.status === 'requires_capture') {
        onSuccess?.(paymentIntent);
      }
    } catch (err) {
      setError('An unexpected error occurred.');
      onError?.(err);
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full h-full  rounded-md">
      {/* Header Section */}
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-medium text-gray-900">{description}</h3>
        <p className="text-3xl font-bold text-gray-900 mt-2">
          {centsToDollars(amount)}
        </p>
      </div>

      {/* Payment Element Section - Scrollable if needed */}
      <div className="flex-1 px-6 py-4 overflow-y-auto">
        <PaymentElement
          id="payment-element"
          options={{
            layout: {
              type: 'tabs',
              defaultCollapsed: false,
            },
          }}
        />
      </div>
      {error && (
        <div className="mt-4 p-3 bg-red-50 text-sm text-red-600 rounded-md">
          {error}
        </div>
      )}
      <div className="mt-4 mx-8 ">
        <button
          id="submit"
          disabled={isProcessing || !stripe}
          className="flex w-full justify-center items-center rounded-md bg-primary text-on-primary p-2 disabled:bg-surface-variant"
        >
          {isProcessing ? (
            <>
              <LoadingOutlined className="animate-spin -ml-1 mr-3 h-5 w-5" />
              Processing...
            </>
          ) : (
            'Pay Now'
          )}
        </button>
      </div>

      {/* </div> */}

      {/* Footer Section - Always visible */}
      {/* <div className="px-6 py-4 bg-gray-50 border-t border-gray-200"> */}

      {/* </div> */}
    </form>
  );
};

const PaymentSheet = ({
  isOpen,
  onClose,
  amount,
  merchantIds,
  platformFee,
  guestId,
  description,
  stripeAccountId,
  currency = 'usd',
  region,
  unique_time,
  onSuccess,
}) => {
  const [clientSecret, setClientSecret] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [customerSession, setCustomerSession] = useState(null);
  useEffect(() => {
    if (isOpen) {
      const initializePayment = async () => {
        setIsLoading(true);
        try {
          const response = await cartApi.createPaymentIntent({
            total: amount,
            merchant_ids: merchantIds,
            platform_fee: platformFee,
            guest_id: guestId,
            description,
            st_account_id: stripeAccountId,
            currency,
            region: region,
            unique_time,
          });

          setClientSecret(response.data.payment_intent.client_secret);
          setCustomerSession(response.data.customer_session.client_secret);
          console.log(process.env.REACT_APP_STRIPE_LIVE_EU);
          const stripe = await loadStripe(
            region == 'europe-west2'
              ? process.env.REACT_APP_ENV == 'production'
                ? process.env.REACT_APP_STRIPE_LIVE_EU
                : process.env.REACT_APP_STRIPE_DEV_EU
              : process.env.REACT_APP_ENV == 'production'
              ? process.env.REACT_APP_STRIPE_LIVE
              : process.env.REACT_APP_STRIPE_DEV,
          );
          setStripePromise(stripe);
        } catch (error) {
          console.error('Failed to initialize payment:', error);
        } finally {
          setIsLoading(false);
        }
      };

      initializePayment();
    }
  }, [isOpen]);

  const handleSuccess = async paymentIntent => {
    message.success('Payment processed! Placing your order...');
    onSuccess(paymentIntent);
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-stretch justify-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-2xl transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="w-full">
                  {isLoading ? (
                    <div className="flex justify-center items-center h-96">
                      <Spin spinning indicator={LoadingIcon}></Spin>
                    </div>
                  ) : clientSecret && stripePromise ? (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        customerSessionClientSecret: customerSession,
                        appearance: {
                          theme: 'stripe',
                          variables: {
                            colorPrimary: '#6366f1',
                          },
                          rules: {
                            '.Tab, .Input': {
                              border: '1px solid #e5e7eb',
                              boxShadow: 'none',
                              fontSize: '14px',
                            },
                            '.Tab:hover': {
                              border: '1px solid #6366f1',
                            },
                            '.Tab--selected': {
                              borderColor: '#6366f1',
                            },
                            '.Label': {
                              fontSize: '14px',
                            },
                          },
                        },
                      }}
                    >
                      <PaymentForm
                        clientSecret={clientSecret}
                        onSuccess={handleSuccess}
                        onError={() => {}}
                        onCancel={onClose}
                        amount={amount}
                        description={description}
                      />
                    </Elements>
                  ) : (
                    <div className="text-center w-full py-12">
                      <p className="text-sm text-gray-500">
                        Failed to load payment form.
                      </p>
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default PaymentSheet;
